import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core';
import RichTextPreview from '../../shared/richTextPreview';
import ActionButton from '../../shared/actionButton';
import SectionTitle from '../../shared/sectionTitle';
import UsersComments from '../../shared/usersComments';
import AlertDialog from '../../shared/alertDialog';
import ManageActionPlan from '../../shared/manageActionPlan';
import ActionPlanDetailsCard from '../../shared/actionPlanDetailsCard';
import ManageTasks from '../../shared/manageTasks';
import CustomButton from '../../shared/customButton';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { GENERAL_CATEGORY_ID } from '../../../constants/tags';
import { ACTION_BUTTON_TYPES } from '../../shared/actionButton/config';
import { ACTION_PLAN_COMMENTS_DEFAULT_PARAMS } from '../../../constants/actionPlan';
import {
  api_action_plan,
  api_action_plan_tasks,
  api_action_plan_comment,
  api_action_plan_comment_update,
} from '../../../constants/apiRoutes';
import {
  isValidParameter,
  replacePropsInObject,
  isUserDeactivated,
  trimString,
} from '../../../utility/helpers';
import http from '../../../utility/http';
import { showSuccessMessage } from '../../../utility/uiUtils';
import { onSaveCreatableTag } from '../../../utility/tagUtils';
import { prepareTasksForSave } from '../../../utility/tasks';
import {
  hasScopedAuthPermissionCheck,
  isUserProfileAccessibleCheck,
} from '../../../utility/permissions';
import {
  EVENT_ACTION_TYPES,
  tagManagerDataLayer,
} from '../../../utility/tagManager';
import { prepareTranslatedActionPlanStatuses } from '../../../utility/actionPlans';

const styles = ({ palette: { primary }, spacing }) => ({
  root: {
    marginTop: spacing(8),
  },
  activity: {
    marginRight: 54,
  },
  subtitle: {
    margin: spacing(8, 0, 4, 0),
  },
  notes: {
    position: 'relative',
    boxSizing: 'border-box',
    border: `1px solid ${primary.bluish6}`,
    padding: spacing(8),
    minHeight: 150,
  },
  editDescriptionButton: {
    position: 'absolute',
    top: 16,
    right: 16,
  },
  type: {
    marginRight: spacing(4),
  },
  loadMoreContainer: {
    marginLeft: spacing(16),
  },
  loadMore: {
    border: `1px solid ${primary.bluish3}`,
    borderRadius: 44,
    color: primary.bluish4,
    lineHeight: '24px',
    justifySelf: 'center',
    padding: spacing(1, 4),
    maxHeight: 32,
  },
});

class ActionPlanDetailsPage extends PureComponent {
  state = {
    isLoading: true,
    isEditOpened: false,
    isDeleteOpened: false,
    isManageTasksOpened: false,
    isDismissRequestOpened: false,
    commentId: 0,
    page: 1,
  };

  componentDidMount() {
    const {
      matchedActionPlanId,
      getActionPlan,
      getActionPlanComments,
      getTagsCategories,
      getAllUsers,
    } = this.props;

    if (isValidParameter(matchedActionPlanId)) {
      return Promise.all([
        getActionPlan(matchedActionPlanId),
        getActionPlanComments(matchedActionPlanId),
        getTagsCategories(),
        getAllUsers(),
      ]).then(() => {
        this.setState({ isLoading: false });
        const { VisitAPDP } = EVENT_ACTION_TYPES;
        tagManagerDataLayer(VisitAPDP.action, VisitAPDP.name);
      });
    }
  }

  componentWillUnmount() {
    const {
      clearActionPlan,
      clearActionPlanComments,
      clearTagsCategories,
      clearAllUsers,
      clearCourses,
      clearCourseEnrolledUsers,
    } = this.props;
    clearActionPlan();
    clearActionPlanComments();
    clearTagsCategories();
    clearAllUsers();
    clearCourses();
    clearCourseEnrolledUsers();
  }

  canAccessUserProfileCheck = userId => {
    const { auth, accessControl } = this.props;

    return isUserProfileAccessibleCheck(
      accessControl,
      auth.id,
      userId?.id || userId
    );
  };

  handleSendComment = (comment, isEdit) => {
    const { actionPlan, getActionPlanComments } = this.props;
    const { commentId } = this.state;

    if (isEdit) {
      return http
        .patch(api_action_plan_comment_update(actionPlan.id, commentId), {
          comment,
        })
        .then(() => {
          this.setState({
            page: ACTION_PLAN_COMMENTS_DEFAULT_PARAMS.page,
            commentId: 0,
          });
          getActionPlanComments(actionPlan.id);
        });
    }

    return http
      .post(api_action_plan_comment(actionPlan.id), { comment })
      .then(() => {
        getActionPlanComments(actionPlan.id);
        this.setState({ page: ACTION_PLAN_COMMENTS_DEFAULT_PARAMS.page });
      });
  };

  onLoadMore = () => {
    const { actionPlan, getActionPlanComments } = this.props;
    const { page } = this.state;
    const incrementedPage = page + 1;
    const params = replacePropsInObject(ACTION_PLAN_COMMENTS_DEFAULT_PARAMS, {
      page: incrementedPage,
    });
    this.setState({ page: incrementedPage });
    return getActionPlanComments(actionPlan.id, params);
  };

  handleDeleteComment = commentId => {
    const { translations, actionPlan, getActionPlanComments } = this.props;

    return http
      .delete(api_action_plan_comment_update(actionPlan.id, commentId))
      .then(() => {
        showSuccessMessage(
          translations.details.comment.deleteCommentDialog.deleteSuccess
        );
        this.setState({ page: ACTION_PLAN_COMMENTS_DEFAULT_PARAMS.page });
        getActionPlanComments(actionPlan.id);
      });
  };

  openEditComment = commentId => {
    this.setState({ commentId });
  };

  closeEditComment = () => {
    this.setState({ commentId: 0 });
  };

  handleActionPlanEdit = () => {
    this.setState({
      isEditOpened: true,
    });
  };

  handleCloseEditActionPlan = () => {
    this.setState({
      isEditOpened: false,
      isDeleteOpened: false,
      isDismissRequestOpened: false,
    });
  };

  handleSaveActionPlan = () => {
    const { getActionPlan, actionPlan } = this.props;
    this.setState({
      isEditOpened: false,
    });
    return Promise.all([getActionPlan(actionPlan.id)]);
  };

  handleOpenManageTasks = () => this.setState({ isManageTasksOpened: true });

  handleCloseManageTasks = () => this.setState({ isManageTasksOpened: false });

  onSaveTasks = async values => {
    const { translations, auth, getActionPlan, actionPlan } = this.props;
    const isCreatedForCurrentUser = auth.id === actionPlan.user.id;
    const tasks = prepareTasksForSave(values.tasks);
    const data = {
      tasks,
      user: actionPlan.user.id,
    };

    if (isCreatedForCurrentUser) {
      await http.post(api_action_plan_tasks(actionPlan.id), data);
    } else {
      await http.patch(api_action_plan(actionPlan.id), data);
    }
    showSuccessMessage(translations.manageTasksDialog.successMessage);

    return getActionPlan(actionPlan.id);
  };

  handleActionPlanDelete = () => this.setState({ isDeleteOpened: true });

  handleCloseActionPlanDelete = () => this.setState({ isDeleteOpened: false });

  handleDismissProposal = () => {
    this.setState({ isDismissRequestOpened: true });
  };

  handleCloseDismissDialog = () => {
    this.setState({ isDismissRequestOpened: false });
  };

  onActionPlanDelete = () => {
    const { translations, navigate, actionPlan } = this.props;
    const { isDismissRequestOpened } = this.state;

    const successMessage = isDismissRequestOpened
      ? translations.dismissActionPlanProposalDialog.deleteSuccess
      : translations.deleteActionPlanDialog.deleteSuccess;

    return http.delete(api_action_plan(actionPlan.id)).then(() => {
      this.handleCloseEditActionPlan();
      showSuccessMessage(successMessage);
      navigate('/action-plans', {
        replace: true,
      });
    }, this.handleCloseEditActionPlan);
  };

  canManageTasksFormCheck = task => {
    const { auth, accessControl, actionPlan } = this.props;

    const canManage = hasScopedAuthPermissionCheck(
      PERMISSION_CODES.PLANS_EDIT,
      accessControl,
      auth.id,
      actionPlan.user?.id
    );
    const isTaskCreatorCurrentUser = task.creator === auth.id;
    const isNewTask = !task.id;

    return canManage || isTaskCreatorCurrentUser || isNewTask;
  };

  canManageTaskCompletionCheck = task => {
    const { auth, accessControl, actionPlan } = this.props;

    const isCurrentPlanManagable = hasScopedAuthPermissionCheck(
      PERMISSION_CODES.PLANS_EDIT,
      accessControl,
      auth.id,
      actionPlan?.user.id
    );
    const isPendingProposal = actionPlan?.is_pending_proposal;

    return (
      !isPendingProposal && (isCurrentPlanManagable || task.user.id === auth.id)
    );
  };

  handleTaskStatusChange = (task, isCurrentPlanManagable) => {
    const { actionPlan, changeActionPlanTaskStatus, changeTaskStatus } =
      this.props;

    return isCurrentPlanManagable
      ? changeActionPlanTaskStatus(actionPlan.id, task.id)
      : changeTaskStatus(task.id);
  };

  onCreateTag = async tagName => {
    const { addMultipleTags } = this.props;
    const tags = await onSaveCreatableTag(tagName);
    const [tag] = tags.create;

    addMultipleTags({ categoryId: GENERAL_CATEGORY_ID, tags });

    return tag;
  };

  handleCoursesSearch = value => {
    const { getCourses } = this.props;
    const text = trimString(value);
    const params = {
      ...(text ? { search: text } : {}),
    };

    return getCourses(params);
  };

  goToUserProfilePage = userId => this.props.navigate(`/people/${userId}`);

  handleClearNotifications = () => {
    const { location, navigate } = this.props;

    return navigate(location.pathname, {
      replace: true,
      state: null,
    });
  };

  render() {
    const {
      classes,
      location,
      auth,
      accessControl,
      translations,
      actionPlan,
      courses,
      actionPlanComments,
      allUsers,
      categories,
      organizationSettings,
      getCourses,
      getCourseEnrolledUsers,
    } = this.props;
    const {
      isLoading,
      isEditOpened,
      isDeleteOpened,
      isManageTasksOpened,
      isDismissRequestOpened,
      commentId,
    } = this.state;
    const { user, description, is_pending_proposal } = actionPlan;
    const { users_see_courses } = organizationSettings;
    const statuses = prepareTranslatedActionPlanStatuses(translations.statuses);
    const isCurrentPlanManagable = hasScopedAuthPermissionCheck(
      PERMISSION_CODES.PLANS_EDIT,
      accessControl,
      auth.id,
      user?.id
    );
    const isCurrentPlanDeletable = hasScopedAuthPermissionCheck(
      PERMISSION_CODES.PLANS_DELETE,
      accessControl,
      auth.id,
      user?.id
    );
    const canDeleteCommentCheck = currentComment =>
      hasScopedAuthPermissionCheck(
        PERMISSION_CODES.PLANS_COMMENTS_DELETE,
        accessControl,
        auth.id,
        currentComment?.creator?.id
      );
    const isUserActive = user && !isUserDeactivated(user);
    const showLoadMore = !!actionPlanComments?.next;
    const isPendingProposal = !!is_pending_proposal;
    const canManageOwnPendingProposal =
      isPendingProposal && user?.id === auth.id;

    return (
      <div>
        {!isLoading ? (
          <div className={classes.root}>
            <ActionPlanDetailsCard
              translations={translations.details}
              auth={auth}
              accessControl={accessControl}
              statuses={statuses}
              actionPlan={actionPlan}
              notifications={location?.state?.notifications}
              isManagable={isCurrentPlanManagable}
              isDeletable={isCurrentPlanDeletable}
              isDisabled={!isUserActive}
              isPendingProposal={isPendingProposal}
              canManageOwnPendingProposal={canManageOwnPendingProposal}
              hasCoursesFeatureEnabled={users_see_courses}
              canManageTaskCompletionCheck={this.canManageTaskCompletionCheck}
              canAccessUserProfileCheck={this.canAccessUserProfileCheck}
              goToUserProfilePage={this.goToUserProfilePage}
              onTaskStatusChange={this.handleTaskStatusChange}
              onDelete={this.handleActionPlanDelete}
              onDismissProposal={this.handleDismissProposal}
              onEdit={this.handleActionPlanEdit}
              onManageTasks={this.handleOpenManageTasks}
              onClearNotifications={this.handleClearNotifications}
            />
            <SectionTitle
              className={classes.subtitle}
              title={translations.details.description}
              variant="h5"
            />
            <div className={classes.notes}>
              <RichTextPreview text={description} />
              {isUserActive &&
                !is_pending_proposal &&
                (isCurrentPlanManagable || canManageOwnPendingProposal) && (
                  <ActionButton
                    className={classes.editDescriptionButton}
                    type={ACTION_BUTTON_TYPES.EDIT}
                    onClickHandler={this.handleActionPlanEdit}
                  />
                )}
            </div>
            {!isPendingProposal ? (
              <UsersComments
                translations={translations.details.comment}
                currentUser={auth}
                commentId={commentId}
                comments={actionPlanComments.results}
                canDeleteCommentCheck={canDeleteCommentCheck}
                onSendComment={this.handleSendComment}
                openEditComment={this.openEditComment}
                onDeleteComment={this.handleDeleteComment}
                closeEditComment={this.closeEditComment}
              />
            ) : null}
            <div className={classes.loadMoreContainer}>
              {showLoadMore && (
                <CustomButton
                  className={classes.loadMore}
                  type="addWithTextRounded"
                  onClick={this.onLoadMore}
                >
                  {translations.loadMore}
                </CustomButton>
              )}
            </div>
          </div>
        ) : null}
        {!isLoading && (
          <ManageActionPlan
            translations={translations.details.manageActionPlan}
            isOpen={isEditOpened}
            auth={auth}
            accessControl={accessControl}
            statuses={statuses}
            actionPlan={actionPlan}
            isInitialValid={!!actionPlan}
            allEmployees={allUsers}
            categories={categories}
            courses={courses.results}
            canSeeCourses={users_see_courses}
            getCourses={getCourses}
            getCourseEnrolledUsers={getCourseEnrolledUsers}
            onCoursesSearch={this.handleCoursesSearch}
            onCreateTag={this.onCreateTag}
            onDelete={this.handleActionPlanDelete}
            onConfirm={this.handleSaveActionPlan}
            onClose={this.handleCloseEditActionPlan}
          />
        )}
        {!isLoading && (
          <ManageTasks
            translations={translations.manageTasksDialog}
            isOpened={isManageTasksOpened}
            record={actionPlan}
            availableUsers={allUsers}
            checkCanManageTask={this.canManageTasksFormCheck}
            onClose={this.handleCloseManageTasks}
            onSave={this.onSaveTasks}
          />
        )}
        <AlertDialog
          isOpened={isDeleteOpened}
          translations={translations.deleteActionPlanDialog}
          onClose={this.handleCloseActionPlanDelete}
          onConfirm={this.onActionPlanDelete}
          isWarning
        />
        <AlertDialog
          translations={translations.dismissActionPlanProposalDialog}
          isOpened={isDismissRequestOpened}
          onClose={this.handleCloseDismissDialog}
          onConfirm={this.onActionPlanDelete}
          isWarning
        />
      </div>
    );
  }
}

ActionPlanDetailsPage.defaultProps = {
  actionPlanComments: {},
};

ActionPlanDetailsPage.propTypes = {
  classes: PropTypes.shape({}).isRequired,
  translations: PropTypes.shape({}).isRequired,
  auth: PropTypes.shape({}).isRequired,
  accessControl: PropTypes.object.isRequired,
  organizationSettings: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  matchedActionPlanId: PropTypes.string.isRequired,
  actionPlan: PropTypes.shape({}).isRequired,
  actionPlanComments: PropTypes.shape({}),
  courses: PropTypes.shape({}).isRequired,
  allUsers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  categories: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  getActionPlan: PropTypes.func.isRequired,
  getActionPlanComments: PropTypes.func.isRequired,
  getTagsCategories: PropTypes.func.isRequired,
  addMultipleTags: PropTypes.func.isRequired,
  getCourses: PropTypes.func.isRequired,
  getCourseEnrolledUsers: PropTypes.func.isRequired,
  clearActionPlan: PropTypes.func.isRequired,
  clearActionPlanComments: PropTypes.func.isRequired,
  clearTagsCategories: PropTypes.func.isRequired,
  getAllUsers: PropTypes.func.isRequired,
  clearAllUsers: PropTypes.func.isRequired,
  changeActionPlanTaskStatus: PropTypes.func.isRequired,
  changeTaskStatus: PropTypes.func.isRequired,
  clearCourses: PropTypes.func.isRequired,
  clearCourseEnrolledUsers: PropTypes.func.isRequired,
};

export default withStyles(styles)(ActionPlanDetailsPage);
